import {
  Auth0AccountRole,
  AutomationChoices,
  AutomationsService,
  BrandKit,
  CRMVisualizationResponse,
  FormFieldCatalog,
  FormFieldInfo,
  MessageTemplateSummary,
  MessageTemplatesService,
  OrganizationResponseForAdmins,
  OrganizationsService,
  TaskRecipientResponse,
  TaskResponse,
  TasksService,
  TaskStatus,
  TemplateCatalog,
  TutorsService,
  VisualizationsService,
  FormsService,
} from "client/openapi";
import { fullName } from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import { errorText } from "components/Forms/formsApi";
import { namesInWords } from "components/MessageTemplates/TemplatePreviewParts";
import type { Person, PersonKey, PersonRole } from "./types";

/*
 * The Tadpole data the Automations area reads: people, templates, the
 * trigger catalog and the editor's choices, CRM boards and fields, forms,
 * tasks and the sending hours. Only GET requests: nothing here writes to
 * Tadpole.
 */

export interface BoardColumn {
  id: number;
  boardId: number;
  /** "board_id:column_id", as triggers name a column. */
  key: string;
  name: string;
  /** "Board › Column". */
  label: string;
  /** Whose cards the column holds. */
  role: PersonRole;
  /** Who is in it in Tadpole now. */
  members: PersonKey[];
}

export interface Board {
  id: number;
  name: string;
  /** Built in (by subject, by payment method): people can't be moved to its columns. */
  builtIn: boolean;
  columns: BoardColumn[];
}

/** A CRM field a step can update. */
export interface CrmField {
  /** Its ref, like "crm:12". */
  field: string;
  label: string;
  /** Whose field it is. */
  role: PersonRole;
  info: FormFieldInfo;
}

export interface Subject {
  id: number;
  name: string;
}

/** One of Tadpole's tasks, for the pickers' examples. */
export interface KnownTask {
  /** "tadpole:<id>". */
  id: string;
  name: string;
  status: TaskStatus;
  assignees: PersonKey[];
  about: PersonKey[];
}

export interface ReferenceData {
  orgId: number;
  orgName: string;
  orgEmail: string | null;
  orgPhone: string | null;
  /** The signed-in person: the admin, or the tutor when the tutor role is picked. */
  me: PersonKey | null;
  timeZone: string;
  /** Messages go out between these hours, in the org's time zone. */
  sendingHours: { from: number; until: number };
  people: Person[];
  personByKey: Record<string, Person>;
  admins: PersonKey[];
  tutors: PersonKey[];
  parents: PersonKey[];
  students: PersonKey[];
  /** student -> their parents */
  parentsOf: Record<string, PersonKey[]>;
  /** parent -> their students */
  studentsOfParent: Record<string, PersonKey[]>;
  /** student -> their tutors */
  tutorsOf: Record<string, PersonKey[]>;
  /** tutor -> their students */
  studentsOfTutor: Record<string, PersonKey[]>;
  /** tutor -> the org subjects they teach (active) */
  subjectsOfTutor: Record<string, number[]>;
  /** student -> their org subjects (active) */
  subjectsOfStudent: Record<string, number[]>;
  subjects: Subject[];
  templates: MessageTemplateSummary[];
  catalog: TemplateCatalog;
  /** What the automation editor offers, from the backend's own lists. */
  choices: AutomationChoices;
  boards: Board[];
  fields: CrmField[];
  /** Tadpole's tasks. */
  tasks: KnownTask[];
  /** What could not be loaded, in plain sentences. */
  problems: string[];
  /** A tutor's own key, by their login (account reference id). */
  tutorOfAccount: Record<string, PersonKey>;
  /**
   * Names the backend gave for people this load doesn't hold (a tutor's
   * login, someone archived since), by key. Filled in as they are read.
   */
  names: Record<string, string>;
}

// ── People keys ───────────────────────────────────────────────────────────

export function personKey(role: PersonRole, id: string | number): PersonKey {
  return `${role}:${id}`;
}

/** The role and id of a key ("admin:auth0|abc" splits at its first colon). */
export function parsePersonKey(key: PersonKey): {
  role: PersonRole;
  id: string;
} {
  const at = key.indexOf(":");
  return {
    role: key.slice(0, at) as PersonRole,
    id: key.slice(at + 1),
  };
}

export function roleOf(key: PersonKey): PersonRole {
  return parsePersonKey(key).role;
}

/** The app's role names, by the API's roles. */
export const ROLE_OF_ACCOUNT_ROLE: Partial<
  Record<Auth0AccountRole, PersonRole>
> = {
  [Auth0AccountRole.ME]: "student",
  [Auth0AccountRole.PARENT]: "parent",
  [Auth0AccountRole.ORG_TUTOR]: "tutor",
  [Auth0AccountRole.ORG_ADMIN]: "admin",
};

export function personOf(
  ref: ReferenceData,
  key: PersonKey | null | undefined
) {
  return key ? ref.personByKey[key] ?? null : null;
}

/** Their name, or "Someone" when they are not in the organization any more. */
export function nameOf(ref: ReferenceData, key: PersonKey | null | undefined) {
  return personOf(ref, key)?.name || (key && ref.names[key]) || "Someone";
}

/** "Rosa Diaz", "Rosa Diaz and Sam Doe", or "Rosa Diaz, Sam Doe, and Amy Smith". */
export function namesOf(ref: ReferenceData, keys: PersonKey[]) {
  return namesInWords(keys.map((key) => nameOf(ref, key)));
}

// ── Loading ───────────────────────────────────────────────────────────────

function emptyCatalog(): TemplateCatalog {
  return {
    variables: [],
    trigger_kinds: [],
    columns: [],
    forms: [],
    date_fields: [],
  };
}

function emptyChoices(): AutomationChoices {
  return {
    groups: [],
    kinds: [],
    actions: [],
    waits: [],
    task_statuses: [],
    approver_roles: [],
    assign_groups: [],
    assign_choices: [],
    shares: [],
    columns: [],
    fields: {},
    subjects: [],
    templates: [],
    forms: [],
    date_fields: [],
    variables: [],
  };
}

function recipientKey(user: TaskRecipientResponse): PersonKey | null {
  if (user.account) return personKey("admin", user.account.reference_id);
  if (user.tutor) return personKey("tutor", user.tutor.id);
  if (user.parent) return personKey("parent", user.parent.id);
  if (user.student) return personKey("student", user.student.id);
  return null;
}

/** One of Tadpole's tasks, as the pickers' examples read it. */
export function tadpoleTask(task: TaskResponse): KnownTask {
  const keys = (users: TaskRecipientResponse[]) =>
    users.map(recipientKey).filter((k): k is PersonKey => k !== null);
  return {
    id: `tadpole:${task.id}`,
    name: task.name,
    status: task.status,
    assignees: keys(task.assigned_users),
    about: keys(task.tagged_users),
  };
}

function boardsFrom(
  boards: CRMVisualizationResponse[],
  keyOf: (role: PersonRole, id: number) => PersonKey
): Board[] {
  return boards.map((board) => ({
    id: board.id,
    name: board.name,
    builtIn: !!board.built_in,
    columns: [...board.columns]
      .sort((a, b) => a.order_index - b.order_index)
      .map((column) => {
        const role = ROLE_OF_ACCOUNT_ROLE[column.column_type] ?? "student";
        const members: PersonKey[] = [
          ...(column.students_in_column ?? []).map((p) =>
            keyOf("student", p.id)
          ),
          ...(column.parents_in_column ?? []).map((p) => keyOf("parent", p.id)),
          ...(column.tutors_in_column ?? []).map((p) => keyOf("tutor", p.id)),
        ];
        return {
          id: column.id,
          boardId: board.id,
          key: `${board.id}:${column.id}`,
          name: column.name,
          label: `${board.name} › ${column.name}`,
          role,
          members,
        };
      }),
  }));
}

function fieldsFrom(catalog: FormFieldCatalog): CrmField[] {
  const of = (role: PersonRole, list: FormFieldInfo[]) =>
    list
      .filter((info) => info.kind === FormFieldInfo.kind.CRM)
      .map((info) => ({ field: info.field, label: info.label, role, info }));
  return [
    ...of("student", catalog.student),
    ...of("parent", catalog.parent),
    ...of("tutor", catalog.tutor),
  ];
}

function peopleFrom(org: OrganizationResponseForAdmins) {
  const people: Person[] = [];
  const add = (
    role: PersonRole,
    id: string | number,
    p: {
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
      phone_number?: string | null;
    }
  ) => {
    const key = personKey(role, id);
    if (people.some((person) => person.key === key)) return;
    people.push({
      key,
      role,
      id: String(id),
      name: fullName(p) || "No name",
      firstName: (p.first_name ?? "").trim(),
      lastName: (p.last_name ?? "").trim(),
      email: p.email || null,
      phone: p.phone_number || null,
    });
  };
  (org.admins ?? []).forEach((admin) =>
    add("admin", admin.reference_id, admin)
  );
  // A tutor's view of the organization has no admins or parents.
  const live = <T extends { archived_at?: string | null }>(list?: T[]) =>
    (list ?? []).filter((p) => !p.archived_at);
  live(org.tutors).forEach((tutor) => add("tutor", tutor.id, tutor));
  live(org.parents).forEach((parent) => add("parent", parent.id, parent));
  live(org.students).forEach((student) => add("student", student.id, student));
  return people;
}

/** Who is signed in, and whether they picked their tutor role. */
export interface Viewer {
  referenceId: string | null;
  tutor: boolean;
}

/** The signed-in tutor's own record in this organization, if they have one. */
async function tutorIdOf(orgId: number): Promise<number | null> {
  try {
    const tutors = await TutorsService.getTutorsMe();
    return tutors.find((t) => t.org_id === orgId)?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Everything the Automations area reads from Tadpole. The organization
 * (its people) and the template catalog are needed; anything else that
 * fails to load is left empty and said in `problems`.
 */
export async function loadReference(
  orgId: number,
  viewer: Viewer
): Promise<ReferenceData> {
  const problems: string[] = [];
  const [
    orgAnswer,
    catalogAnswer,
    templatesAnswer,
    boardsAnswer,
    fieldsAnswer,
    tasksAnswer,
    brandAnswer,
    choicesAnswer,
  ] = await Promise.allSettled([
    OrganizationsService.getOrganization({ orgId }),
    MessageTemplatesService.getMessageTemplateCatalog({ orgId }),
    MessageTemplatesService.getMessageTemplates({ orgId }),
    VisualizationsService.getVisualizations({ orgId }),
    FormsService.getFormFieldCatalog({ orgId }),
    TasksService.getOrgTasks({ orgId }),
    MessageTemplatesService.getMessageTemplateBrand({ orgId }),
    AutomationsService.getAutomationChoices({ orgId }),
  ]);
  const tutorId = viewer.tutor ? await tutorIdOf(orgId) : null;

  if (orgAnswer.status === "rejected")
    throw new Error(
      errorText(
        orgAnswer.reason,
        "The organization's people could not be loaded."
      )
    );
  // A tutor answers cards on their Tasks: the editor's catalog and
  // choices are admins' only, so for them they are left empty.
  if (catalogAnswer.status === "rejected" && !viewer.tutor)
    throw new Error(
      errorText(
        catalogAnswer.reason,
        "The kinds of triggers could not be loaded."
      )
    );
  if (choicesAnswer.status === "rejected" && !viewer.tutor)
    throw new Error(
      errorText(
        choicesAnswer.reason,
        "What automations can do could not be loaded."
      )
    );
  const org = orgAnswer.value as OrganizationResponseForAdmins;
  const catalog =
    (catalogAnswer.status === "fulfilled" && catalogAnswer.value) ||
    emptyCatalog();
  const choices =
    (choicesAnswer.status === "fulfilled" && choicesAnswer.value) ||
    emptyChoices();

  const settled = <T>(
    answer: PromiseSettledResult<T>,
    fallback: T,
    what: string
  ): T => {
    if (answer.status === "fulfilled") return answer.value;
    problems.push(`${what} could not be loaded.`);
    return fallback;
  };
  const templates = settled(
    templatesAnswer,
    [] as MessageTemplateSummary[],
    "The message templates"
  );
  const rawBoards = settled(
    boardsAnswer,
    [] as CRMVisualizationResponse[],
    "The CRM boards"
  );
  const fieldCatalog = settled(
    fieldsAnswer,
    { parent: [], student: [], tutor: [] } as FormFieldCatalog,
    "The CRM fields"
  );
  const rawTasks = settled(tasksAnswer, [] as TaskResponse[], "The tasks");
  const brand = settled(
    brandAnswer,
    null as BrandKit | null,
    "The sending hours"
  );

  const people = peopleFrom(org);
  const personByKey: Record<string, Person> = {};
  people.forEach((person) => {
    personByKey[person.key] = person;
  });
  const known = (key: PersonKey) => !!personByKey[key];
  const keysOf = (role: PersonRole) =>
    people.filter((p) => p.role === role).map((p) => p.key);

  const parentsOf: Record<string, PersonKey[]> = {};
  const studentsOfParent: Record<string, PersonKey[]> = {};
  const tutorsOf: Record<string, PersonKey[]> = {};
  const studentsOfTutor: Record<string, PersonKey[]> = {};
  const subjectsOfTutor: Record<string, number[]> = {};
  const subjectsOfStudent: Record<string, number[]> = {};
  const link = (
    map: Record<string, PersonKey[]>,
    from: PersonKey,
    to: PersonKey
  ) => {
    if (!known(from) || !known(to)) return;
    map[from] = map[from] ?? [];
    if (!map[from].includes(to)) map[from].push(to);
  };
  (org.parents ?? []).forEach((parent) =>
    (parent.students ?? []).forEach((student) => {
      const p = personKey("parent", parent.id);
      const s = personKey("student", student.id);
      link(studentsOfParent, p, s);
      link(parentsOf, s, p);
    })
  );
  const tutorOfAccount: Record<string, PersonKey> = {};
  (org.tutors ?? []).forEach((tutor) => {
    const t = personKey("tutor", tutor.id);
    (tutor.students ?? []).forEach((student) => {
      const s = personKey("student", student.id);
      link(studentsOfTutor, t, s);
      link(tutorsOf, s, t);
    });
    if (known(t)) {
      subjectsOfTutor[t] = (tutor.subjects ?? [])
        .filter((subject) => subject.active)
        .map((subject) => subject.org_subject.id);
      if (tutor.account_id) tutorOfAccount[tutor.account_id] = t;
    }
  });
  org.students.forEach((student) => {
    const s = personKey("student", student.id);
    if (known(s))
      subjectsOfStudent[s] = (student.subjects ?? [])
        .filter((subject) => subject.active)
        .map((subject) => subject.org_subject.id);
  });

  const me = viewer.tutor
    ? tutorId !== null
      ? personKey("tutor", tutorId)
      : null
    : viewer.referenceId
    ? personKey("admin", viewer.referenceId)
    : null;
  if (viewer.tutor && viewer.referenceId && tutorId !== null)
    tutorOfAccount[viewer.referenceId] = personKey("tutor", tutorId);

  return {
    orgId,
    orgName: org.name,
    orgEmail: org.public_email ?? null,
    orgPhone: org.public_phone_number ?? null,
    me: me && known(me) ? me : null,
    timeZone:
      brand?.time_zone ||
      org.default_timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    sendingHours: {
      from: brand?.send_from_hour ?? 8,
      until: brand?.send_until_hour ?? 20,
    },
    people,
    personByKey,
    admins: keysOf("admin"),
    tutors: keysOf("tutor"),
    parents: keysOf("parent"),
    students: keysOf("student"),
    parentsOf,
    studentsOfParent,
    tutorsOf,
    studentsOfTutor,
    subjectsOfTutor,
    subjectsOfStudent,
    subjects: (org.subjects ?? [])
      .filter((subject) => subject.active)
      .map((subject) => ({ id: subject.id, name: subject.name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    templates,
    catalog,
    choices,
    boards: boardsFrom(rawBoards, personKey),
    fields: fieldsFrom(fieldCatalog),
    tasks: rawTasks.map(tadpoleTask),
    problems,
    tutorOfAccount,
    names: {},
  };
}

// ── Lookups ───────────────────────────────────────────────────────────────

export function columnByKey(
  ref: ReferenceData,
  key: string | null | undefined
) {
  if (!key) return null;
  for (const board of ref.boards) {
    const column = board.columns.find((c) => c.key === key);
    if (column) return { board, column };
  }
  return null;
}

export function columnById(
  ref: ReferenceData,
  boardId: number | null,
  columnId: number | null
) {
  if (boardId === null || columnId === null) return null;
  return columnByKey(ref, `${boardId}:${columnId}`);
}

/** The catalog's label for a column ("Board › Column"), even one not loaded. */
export function columnLabel(
  ref: ReferenceData,
  key: string | null | undefined
) {
  if (!key) return "a CRM column";
  return (
    columnByKey(ref, key)?.column.label ??
    ref.catalog.columns.find((c) => c.value === key)?.label ??
    "a CRM column"
  );
}

/** A column as the editor's choices know it: whether a move can put someone in it, and what it sets or needs. */
export function columnInfo(ref: ReferenceData, key: string | null | undefined) {
  if (!key) return null;
  return ref.choices.columns.find((c) => c.column === key) ?? null;
}

/** A kind of event as the editor's choices know it (its timing, whether it repeats, what its runs carry). */
export function kindInfo(ref: ReferenceData, kind: string) {
  return ref.choices.kinds.find((k) => k.kind === kind) ?? null;
}

export function formName(
  ref: ReferenceData,
  formId: string | number | null | undefined
) {
  if (formId === null || formId === undefined || formId === "") return "a form";
  return (
    ref.catalog.forms.find((f) => f.value === String(formId))?.label ?? "a form"
  );
}

export function templateById(
  ref: ReferenceData,
  id: number | null | undefined
) {
  if (id === null || id === undefined) return null;
  return ref.templates.find((t) => t.id === id) ?? null;
}

export function fieldByRef(
  ref: ReferenceData,
  field: string | null | undefined
) {
  if (!field) return null;
  return ref.fields.find((f) => f.field === field) ?? null;
}

export function subjectName(ref: ReferenceData, id: number | null | undefined) {
  if (id === null || id === undefined) return null;
  return ref.subjects.find((s) => s.id === id)?.name ?? null;
}
